'use client';

import { useFormContext } from 'react-hook-form';
import { type FormSchema, workplaceSchema } from '@/app/profile/edit/schema';
import { useState, useEffect } from 'react';
import type {
  SearchCompaniesRes,
  Company
} from '@/app/register/_workplace/types';
import { Input } from '@/components/shadcn/Input';
import { z } from 'zod';
import { usePathname } from 'next/navigation';
import { otherCompanyId } from '@/app/register/_workplace/schema';
import { initialWorkplace } from '@/app/register/_workplace/schema';

const fetchCompanies = async (name: string): Promise<SearchCompaniesRes> => {
  const res = await fetch(
    `${
      process.env.NEXT_PUBLIC_CLOUDFUNCTIONS_END_POINT
    }/v2-callable-company-company?name=${name}${name ? '' : '&limit=0'}`
  );

  if (!res.ok) {
    throw new Error('Failed to fetch data');
  }
  return await res.json();
};

type Props = {
  value: string;
  index: number;
  className?: string;
};

export default function CompanyName({ value, index, className }: Props) {
  const { getValues, setValue, watch } = useFormContext<FormSchema>();
  const [errorMessage, setErrorMessage] = useState('');

  const [timerId, setTimerId] = useState<NodeJS.Timeout>();

  const [suggestions, setSuggestions] = useState<Company[][]>(
    Array(4).fill([])
  );

  const updateSuggestions = (name: string, index: number) => {
    clearTimeout(timerId);

    const timer = setTimeout(async () => {
      const { data } = await fetchCompanies(name);
      const clonedSuggestions = [...suggestions];
      clonedSuggestions.splice(index, 1, data);
      setSuggestions(clonedSuggestions);
    }, 500);

    setTimerId(timer);
  };

  const inputText = (name: string, index: number) => {
    const workplace = getValues(`workplace.${index}`);
    const value = name
      ? {
          ...workplace,
          companyOrder: index + 1,
          companyId: otherCompanyId,
          companyName: name
        }
      : {
          ...workplace,
          companyOrder: index + 1,
          companyId: initialWorkplace.companyId,
          companyName: name
        };

    setValue(`workplace.${index}`, value, { shouldDirty: true });
  };

  const selectSuggestion = (index: number, { id, name }: Company) => {
    const workplace = getValues(`workplace.${index}`);
    setValue(
      `workplace.${index}`,
      {
        ...workplace,
        companyOrder: index + 1,
        companyId: id,
        companyName: name
      },
      { shouldDirty: true }
    );

    const clonedSuggestions = [...suggestions];
    clonedSuggestions.splice(index, 1, []);
    setSuggestions(clonedSuggestions);
  };

  const pathname = usePathname();
  const companyName = watch(`workplace.${index}.companyName`);

  useEffect(() => {
    if (pathname.includes('register')) return;
    try {
      workplaceSchema.shape.companyName.parse(companyName);
      setErrorMessage('');
    } catch (e) {
      if (e instanceof z.ZodError) {
        setErrorMessage(e.flatten().formErrors[0]);
      }
    }
  }, [companyName, pathname]);

  return (
    <>
      <p className="mb-[8px] ml-[5px] text-[12px] font-semibold text-[#231717]">
        企業名
      </p>
      <Input
        value={value}
        type="text"
        placeholder="企業名"
        onChange={e => {
          inputText(e.target.value, index);
          updateSuggestions(e.target.value, index);
        }}
        className={className}
      />

      {suggestions[index].length ? (
        <ul className="max-h-[300px] overflow-y-scroll bg-white">
          {suggestions[index].map(({ id, name, industryNumber }) => (
            <li
              key={id}
              onClick={() =>
                selectSuggestion(index, { id, name, industryNumber })
              }
              className="pl-[10px] leading-[48px] opacity-80 hover:cursor-pointer hover:bg-[#bbb]"
            >
              {name}
            </li>
          ))}
        </ul>
      ) : null}
      {errorMessage ? (
        <p className="ml-[10px] mt-[5px] text-[12px] font-semibold text-[#d92f2b]">
          {errorMessage}
        </p>
      ) : null}
    </>
  );
}
